import { FileInfo, Widget, WidgetAPI } from "@uploadcare/react-widget";
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import {
  MdClose,
  MdImage,
  MdOutlineCloudUpload,
  MdOutlineTextSnippet,
} from "react-icons/md";
import { config } from "server/config";
import { FILE_EXTENSIONS_WHITELIST } from "utils/FILE_EXTENSIONS_WHITELIST";
import { allowedFileTypesConfigToArray } from "blocks/v2/Form/Field/Editor/EditorFields/FileUploadFields";
import { cn } from "utils/cn";

const Wrapper: React.FunctionComponent<React.ComponentPropsWithRef<"div">> = (
  props
) => (
  <div
    {...props}
    className={
      // prettier-ignore
      cn(
        "flex h-20 items-center justify-between rounded-lg border-2 border-dashed border-zi-lightGray bg-zi-lightGray/10 px-5 py-0 custom-uploadcare-widget",
        props.className
      )
    }
  />
);

const Placeholder: React.FunctionComponent<
  React.ComponentPropsWithoutRef<"div">
> = (props) => (
  <div
    {...props}
    className={cn(
      "flex shrink items-center text-base font-normal not-italic text-zi-darkGray",
      props.className
    )}
  />
);

type Props = {
  placeholder?: string;
  value?: FileInfo | null;
  allowedFileTypes?: string;
  onChange: (value?: FileInfo) => void;
};

interface SignedUpload {
  signature: string;
  expire: number;
}

export const FileUpload = forwardRef<HTMLDivElement, Props>(
  ({ placeholder, ...props }, ref) => {
    const widgetRef = useRef<WidgetAPI>(null);

    const [signedUpload, setSignedUpload] = useState(
      undefined as unknown as SignedUpload
    );
    const [file, setFile] = useState(undefined as unknown as FileInfo);
    const [fileSelected, setFileSelected] = useState(false);

    const getSignedUpload = useCallback(async () => {
      const response = await axios.post("/api/uploadcare-signature");
      setSignedUpload(response.data);
    }, []);

    useEffect(() => {
      getSignedUpload();
    }, [getSignedUpload]);

    const acceptTypes = useMemo(() => {
      const defaultAllowList = FILE_EXTENSIONS_WHITELIST.map(
        (ext) => `.${ext}`
      ).join(",");
      if (!props.allowedFileTypes) return defaultAllowList;
      const allowedFileTypes = allowedFileTypesConfigToArray(
        props.allowedFileTypes
      ).filter((ext) => ext !== "*");

      const mimeTypes = allowedFileTypes
        .filter((ext) => FILE_EXTENSIONS_WHITELIST.includes(ext))
        .map((ext) => `.${ext}`);

      return mimeTypes.length ? mimeTypes.join(",") : defaultAllowList;
    }, [props.allowedFileTypes]);

    const fileExtensionValidator = useCallback(
      (fileInfo: FileInfo) => {
        if (
          !props.allowedFileTypes ||
          fileInfo.sourceInfo?.source === "uploaded"
        )
          return;
        const allowedFileTypes = allowedFileTypesConfigToArray(
          props.allowedFileTypes
        ).filter((ext) => ext !== "*");

        const ext = fileInfo.name?.split(".").pop()?.toLowerCase();

        if (!ext || !allowedFileTypes.includes(ext)) {
          throw new Error("fileType");
        }
      },
      [props.allowedFileTypes]
    );

    const onFileSelect = useCallback(
      (e: any) => {
        setFileSelected(e !== null);
        if (e !== null) {
          e.done((file: FileInfo) => {
            setFile(file);
            props.onChange(file);
          });
        } else {
          props.onChange(undefined);
        }
      },
      [props]
    );

    const reset = useCallback(() => {
      const widget = widgetRef.current;
      if (widget) {
        widget.value(null);
      }
      setSignedUpload(undefined as any);
      setFile(undefined as any);
      setFileSelected(false);
      getSignedUpload();
    }, [getSignedUpload]);

    useEffect(() => {
      if (!props.value) reset();
    }, [props.value, reset]);

    if (!signedUpload) {
      return (
        <Wrapper>
          <Placeholder>
            <MdOutlineCloudUpload size={28} style={{ marginRight: 10 }} />
            {placeholder || "Drag and drop files here"}
          </Placeholder>
        </Wrapper>
      );
    }

    const renderFileIcon = () => {
      switch (file.mimeType) {
        case "image/jpg":
        case "image/jpeg":
          return <MdImage size={28} style={{ marginRight: 10 }} />;
        default:
          return <MdOutlineTextSnippet size={28} style={{ marginRight: 10 }} />;
      }
    };

    return (
      <Wrapper ref={ref}>
        <div
          className={cn("flex items-center justify-between", {
            "grow-0": !!file,
            grow: !file,
          })}
        >
          <Placeholder>
            {!fileSelected && (
              <>
                <MdOutlineCloudUpload size={28} style={{ marginRight: 10 }} />
                {placeholder || "Drag and drop files here"}
              </>
            )}

            {file && renderFileIcon()}
          </Placeholder>

          <Widget
            ref={widgetRef}
            publicKey={config().NEXT_PUBLIC_UPLOADCARE_PUBLIC_KEY}
            secureSignature={signedUpload?.signature}
            secureExpire={signedUpload?.expire}
            onFileSelect={onFileSelect}
            tabs="file camera url facebook gdrive gphotos"
            localeTranslations={{
              buttons: { choose: { files: { one: "Browse files" } } },
              errors: {
                // @ts-ignore
                fileType: "File type not supported",
              },
            }}
            value={file?.cdnUrl ?? ""}
            inputAcceptTypes={acceptTypes}
            validators={[fileExtensionValidator]}
          />
        </div>

        {file && (
          <MdClose size={28} onClick={reset} style={{ cursor: "pointer" }} />
        )}
      </Wrapper>
    );
  }
);

FileUpload.displayName = "FileUpload";
